/** The domain page in the owner's v2.1 spec: header, status ribbon, tab grid, side columns and the twelve tabs. */
export const domainPage = {
  header: {
    copy: 'Copy',
    copied: 'Copied',
    picker: 'Domain shown',
    export: 'Export details',
  },
  ribbon: {
    service: 'Service',
    registry: 'Registry',
    dossier: 'Dossier',
    billing: 'Payment',
    sync: 'Sync',
    dnssec: 'DNSSEC',
    loading: 'Loading…',
    stopped: 'Registration stopped',
    unresolved_one: '{{count}} command without a result',
    unresolved_other: '{{count}} commands without a result',
    registryOk: 'Normal',
    registryNone: 'Not read yet',
    inSync: 'In sync',
    drift_one: '{{count}} difference',
    drift_other: '{{count}} differences',
    notCompared: 'Not compared yet',
    unavailable: 'Not available',
    dnssecOn: 'Configured',
    dnssecOff: 'Off',
  },
  tabs: {
    label: 'Parts of the domain',
    mobile: 'Showing',
    overview: 'Overview',
    subject: 'Registrant & contacts',
    dossier: 'Dossier & identity',
    dns: 'DNS & name servers',
    dnssec: 'DNSSEC',
    addons: 'Add-ons & fees',
    finance: 'Finance',
    lifecycle: 'Lifecycle',
    transfer: 'Transfer & security',
    registry: 'Registry',
    compliance: 'Compliance & sync',
    history: 'History & notes',
  },
  context: {
    title: 'Domain management',
    noSubject: 'No registrant yet',
    customer: 'Account',
    dossier: 'Current dossier',
    policy: 'Rules',
    registered: 'Registered',
    openSubject: 'Registrant details',
    back: 'Back to the list',
  },
  actions: {
    title: 'Domain actions',
    statusTitle: 'Locks & auto-renew',
    hold: 'Domain hold',
    updateLock: 'Update lock',
    subtitle: 'Each change asks for a reason and is kept in the log.',
    autoRenew: 'Auto-renew',
    autoRenewOn: 'On · {{years}} yr',
    transferLock: 'Transfer lock',
    on: 'On',
    off: 'Off',
    redemption: 'The domain is {{state}}: normal renewal does not apply.',
    unresolved: 'A registry command has no result yet: it is being reconciled first.',
    notActive: 'Renewal is not possible while the domain is {{state}}.',
  },
  checks: {
    title: 'Renewal conditions',
    subtitle: 'What this page can see; the renewal checks again when it runs.',
    dossier: 'Dossier complete',
    registry: 'Every registry command has a result',
    state: 'The domain’s state allows renewal',
    locks: 'No legal lock',
    balance: 'Balance: checked when the renewal runs',
    met: 'Met',
    notMet: 'Not met',
    later: 'Checked later',
  },
  subject: {
    title: 'Registrant of the domain',
    titleHint: 'The legal registrant, separate from the paying account.',
    failed: 'Could not load the registrant',
    editSubject: 'Edit registrant',
    pinned: 'Version {{n}} in the dossier',
    pinnedOld: 'Dossier has version {{pinned}} (now {{current}})',
    notVerified: 'Not verified',
    noProfile: 'The registrant’s details are not available.',
    fullName: 'Full name',
    legalName: 'Legal name',
    tradeName: 'Trade name',
    position: 'Position',
    birthDate: 'Date of birth',
    birthMissing: 'Not on file',
    gender: 'Gender',
    idNumber: 'Identity number',
    idAndTax: 'Citizen ID (also the tax code)',
    taxCode: 'Tax code / enterprise code',
    householdTax: 'Tax code (owner’s citizen ID)',
    founded: 'Founded',
    nationality: 'Nationality',
    country: 'Country',
    phone: 'Phone',
    email: 'E-mail',
    fax: 'Fax',
    address: 'Address',
    headOffice: 'Head office',
    representative: 'Legal representative',
    owner: 'Household owner',
    representativeHint: 'An individual registrant of its own, with every detail an individual has.',
    representativeFailed: 'Could not load the representative',
    openRepresentative: 'Open',
    noRepresentative: 'No legal representative yet',
    noRepresentativeBody: 'Link an individual as the legal representative before the dossier or a registry update.',
    since: 'Representative since {{date}}',
  },
  contacts: {
    atRegistry: 'Compare with the registry',
    title: 'The domain’s contacts',
    hint: 'Registrant, admin, tech and billing belong to this domain: copied from the registrant at registration; changing one changes this domain only.',
    differs: 'differs from the registrant now',
    origins: { CLONED: 'Copied at registration', SYNCED: 'Synced from the registrant', EDITED: 'Edited for this domain' },
    syncing: 'Updating at the registry',
    drift_one: '{{count}} field differs',
    drift_other: '{{count}} fields differ',
    matches: 'Same as the registrant',
    edit: 'Change',
    registryId: 'ID at the registry',
    noHandle: 'This registry keeps no contact IDs',
    organization: 'Organisation',
    name: 'Name',
    position: 'Position',
    email: 'E-mail',
    phone: 'Phone',
    fax: 'Fax',
    address: 'Address',
    ids: 'Identity documents',
    nationality: 'Nationality',
    noDetails: 'The contact’s details are not shown to your role.',
    fromSubject: 'From',
    version: 'version {{n}}',
    notCurrent: 'not the current version',
    since: 'since {{date}}',
    copiedAt: 'first copied {{date}}',
    source: 'Source',
    showPast_one: 'Earlier contacts ({{count}})',
    showPast_other: 'Earlier contacts ({{count}})',
    hidePast: 'Hide earlier contacts',
    driftTitle_one: '{{count}} contact differs from its registrant now',
    driftTitle_other: '{{count}} contacts differ from their registrants now',
    driftBody: 'The highlighted fields changed on the registrant after they were copied. Nothing changes on the domain until staff choose to sync.',
    syncFromSubject: 'Sync from the registrant',
    syncFrom: 'Sync from {{name}}',
    syncTitle: 'Sync contacts from the registrant · {{domain}}',
    syncDescription_one: 'The {{roles}} contact, which differs, takes the current details of {{name}}, for this domain only.',
    syncDescription_other: 'The {{count}} contacts that differ ({{roles}}) take the current details of {{name}}, for this domain only.',
    theirSubject: 'the contact’s own registrant',
    syncResult: {
      STARTED: 'Started: the registry is being updated.',
      REGISTRANT_CHANGE: 'The registrant itself changed: a registrant change request was opened.',
      UP_TO_DATE: 'Already the same as the registrant.',
      SKIPPED: 'Not synced (see the reason on the registrant page).',
    },
  },
  scope: {
    title: 'Account & what a change touches',
    hint: 'A change here never overwrites a verified identity or another domain’s contacts.',
    account: 'Paying account',
    domain: 'Domain shown',
    change: 'A change here',
    changeValue: 'Applies to this domain only',
    others: 'Other domains of the registrant',
    none: 'None',
    ownershipTitle: 'Changing the registrant is its own procedure',
    ownershipBody: 'The registrant is not changed by editing a name, an e-mail or the paying account.',
    ownershipLink: 'Open Transfer & security',
  },
  dossierPanel: {
    title: 'Dossier',
    version: 'Version {{n}}',
    history: 'History',
    open: 'Open the dossier',
    next: {
      title: 'Process dossier',
      approve: 'Approve dossier',
      approvePaper: 'Approve paper declaration',
      review: 'Compare the documents and checks below, then approve or refuse here. The decision applies to the dossier, not to an individual file.',
      individual: 'An individual needs eKYC. Staff can upload documents here; approving paper cannot replace eKYC. After the customer verifies their identity, the declaration proceeds to the registrar signature.',
      paper: 'Download the template, obtain the signed and stamped copy, then choose “Upload documents manually”. Once the paper checks finish, “Approve paper declaration” appears here.',
      checking: 'The submitted declaration is being checked. Staff can approve the paper here once the checks finish.',
      sign: 'The registrant is verified. Check the files below and send the declaration for the registrar signature to complete the dossier.',
      complete: 'The dossier is complete. The VNNIC submission result appears below.',
      remindEkyc: 'Remind customer to complete eKYC',
    },
    fileState: { GENERATED: 'Template', STORED: 'Stored', AWAITING_IDENTITY: 'Awaiting identity', AWAITING_CHECK: 'Being checked' },
    upload: {
      title: 'Upload to the dossier',
      kind: 'File kind',
      file: 'File',
      submit: 'Upload',
      manual: 'Upload documents manually',
      template: 'Download form',
      templateHint: 'Download the domain declaration filled with the registrant’s details',
      noTemplate: 'The domain has no declaration to download',
      ekyc: 'eKYC',
      identity: 'View ID card details',
      identityOrg: 'View registrant details',
      requiredOption: '{{name}} — required',
      noSlots: 'Staff cannot upload any file kind for this dossier',
      pickFile: 'Pick a file kind and a file first',
      reason: 'Reason (recorded in the log)',
      dialogTitle: 'Upload: {{kind}}',
      badExtension: 'This document type takes {{list}} files only.',
      tooLarge: 'The file is too large: at most {{max}} for this document type.',
      tooSmall: 'VNNIC only takes files of {{min}} or more for this type.',
      dialogBody: '“{{file}}” is attached to the dossier; a PDF’s digital signature is checked automatically.',
      done: 'Uploaded.',
      declarationBody: '“{{file}}” goes through the same checks as a declaration the customer sends: a signed PDF’s digital signature is verified, a scan is read and decided by staff, then the registrar signs.',
      individualDeclarationTitle: 'An individual still needs eKYC',
      individualDeclaration: 'An individual still needs eKYC; the uploaded declaration is only stored in the dossier. It does not replace eKYC and does not verify the registrant.',
      declarationDone: 'The declaration is in the checks: signature / paper review, then a staff decision and the registrar’s signature.',
      downloaded: 'Declaration downloaded.',
    },
    checklist: {
      title: 'Quick check',
      declared: 'Domain declaration exists',
      accepted: 'Declaration accepted',
      verified: 'Registrant verified',
      subject: 'Dossier of the current registrant',
      files: 'Every file VNNIC requires',
      registrar: 'Registrar signed the declaration',
      required: 'Required: {{name}}',
      requiredEkyc: 'Required: {{name}} (met by eKYC)',
      vnnic: { APPROVED: 'Approved by VNNIC', PENDING: 'Sent to VNNIC, awaiting approval', REJECTED: 'Refused by VNNIC', NOT_SENT: 'Not sent to VNNIC' },
      vnnicUnknown: 'Sent to VNNIC',
      state: { ok: 'Done', waiting: 'Waiting', missing: 'Not done', unknown: 'Unknown' },
    },
    files: {
      title: 'Uploaded files',
      kind: 'File kind',
      status: 'Status',
      vnnic: 'Sent to VNNIC',
      dates: 'Created / updated',
      file: 'File',
      actions: 'Actions',
      sent: 'Sent',
      notSent: 'Not sent',
      view: 'View',
      download: 'Download',
      details: 'Document details',
      readTitle: 'Open {{name}}',
      readBody: 'Dossier files hold identity data: opening one is logged with the reason and needs the permission to view identity documents.',
      previewHint: 'The platform’s own copy of the file, decrypted for viewing.',
      registrarSigned: 'Registrar signed',
      reason: 'Reason: {{reason}}',
      created: 'Created {{date}}',
      updated: 'Updated {{date}}',
      none: 'The dossier has no file yet',
      older: 'Older versions ({{count}}): a newer file of the same kind replaced them; history only',
      failed: 'Could not load the files',
      approve: 'Approve',
      reject: 'Refuse',
      replace: 'Replace',
      requestMore: 'Ask for more',
      noReview: 'Nothing to decide for this dossier',
      notPending: 'This file is not waiting for a decision',
      notReplaceable: 'This file kind is not in the upload list of this dossier',
      noRequest: 'The dossier is not waiting in the dossier list: nothing to ask for',
    },
    state: { APPROVED: 'Approved', PENDING: 'Pending', REJECTED: 'Refused' },
    reason: {
      EKYC_AUTO: 'Confirmed automatically by eKYC',
      SIGNATURE_AUTO: 'Confirmed automatically (valid digital signature)',
      STAFF_APPROVED: 'Approved by staff',
      AUTO_CHECK: 'Automatic check passed',
      ORG_DECLARATION: 'Generated from the subject; approved with the signed organisation information (#27)',
      AWAITING_REGISTRAR: 'Waiting for the registrar’s signature',
      AWAITING_STAFF: 'Waiting for staff',
      GENERATED: 'Generated template, not a signed copy submitted by the customer',
      STORED: 'Stored in the dossier; no staff review is open for this file',
      AWAITING_IDENTITY: 'The individual must complete eKYC before the dossier can be completed',
      AWAITING_CHECK: 'The submitted file is being checked automatically',
      FORMAT: 'File format not allowed by VNNIC',
      SIZE: 'File size not allowed by VNNIC',
      DIMENSIONS: 'Image size not allowed by VNNIC',
      CHECK_FAILED: 'Automatic check failed',
      OTHER: 'The file cannot be used for the VNNIC dossier',
    },
    fileType: { 1: 'ID card, front', 2: 'ID card, back', 3: 'Passport', 4: 'Business licence', 5: 'Domain declaration', 6: 'gov.vn commitment', 7: 'Sensitive keyword commitment', 15: 'Registrar change declaration', 16: 'Organisation declaration' },
    sign: {
      button: 'Sign (SmartCA)',
      title: 'Registrar signature: {{kind}}',
      body: 'The registrar signs the file with SmartCA. “Sign now” sends the confirmation to the SmartCA app now; “Daily batch {{time}}” signs it with the other dossiers of the day.',
      now: 'Sign now',
      batch: 'Daily batch {{time}}',
      queued: 'In the {{time}} batch',
      inBatch: 'Waiting for confirmation in the app',
      signed: 'Signed',
      expired: 'Expired — send again',
      checking: 'Being checked before signing',
      review: 'The dossier needs fixing before signing',
      unavailable: 'Only a PDF not yet signed by the registrar can be sent for signing',
      alreadyQueued: 'The file is already in the {{time}} batch.',
      sent: 'Sent for signing. Confirm in the SmartCA app.',
      failed: 'The signing request could not be sent. Reload the page and try again.',
      queuedDone: 'Added to the {{time}} batch.',
      acceptedWhenSigning: 'The declaration was approved by the signing request.',
    },
    ekyc: {
      title: 'eKYC result',
      score: 'eKYC result (face match)',
      requestId: 'eKYC request ID',
      verifiedAt: 'Verified at',
      provider: 'Provider',
      result: 'Result',
      none: 'No eKYC result. Customers do eKYC on the customer portal; staff cannot run it for them on this page.',
      org: 'An organisation is verified by digital signature, not eKYC.',
    },
    identity: {
      title: 'Registrant details (masked)',
      name: 'Name',
      ids: 'Documents',
      birth: 'Date of birth',
      birthOnFile: 'On file (not shown)',
      nationality: 'Nationality',
      address: 'Address',
      signer: 'Declaration signed by',
      masked: 'Document numbers are masked; the full number is shown only on the registrant’s page (logged).',
      openSubject: 'Open the registrant',
      failed: 'Could not load the registrant',
      none: 'The dossier has no registrant.',
    },
    vnnic: {
      notReady: 'the dossier is incomplete; finish the steps above before submission',
      title: 'Dossier at VNNIC',
      sync: 'Sync',
      syncHint: 'Refresh the dossier’s status at VNNIC',
      type: 'Kind',
      id: 'VNNIC dossier ID',
      createdBy: 'Created by',
      createdAt: 'Created',
      updatedBy: 'Updated by',
      updatedAt: 'Updated',
      method: 'Verification',
      methodValue: '#{{n}} {{name}}',
      ekycScore: 'eKYC result',
      ekycRequest: 'eKYC request ID',
      signer: 'Signed by',
      signedAt: 'Signed on',
      status: 'Dossier status',
      statusValue: { APPROVED: 'Approved', PENDING: 'Pending', REJECTED: 'Refused', NOT_SENT: 'Not sent' },
      notSent: 'Not sent to VNNIC',
      resend: 'To fix before sending again',
      missing: 'still missing: {{list}}',
      ready: 'ready, sent automatically',
      fileId: 'ID',
      fileName: 'File name',
      fileSize: 'Size',
      fileType: 'Kind',
      fileSign: 'Signed',
      fileView: 'Preview the file',
      fileDownload: 'Download the file',
      fileUnavailable: 'VNNIC has not returned the file yet; try again later',
      filePreviewHint: 'The file as VNNIC holds it (or the platform copy before it is sent).',
      fileReadTitle: 'Open {{name}}',
      fileReadBody: 'Dossier files hold identity data: every opening is logged with its reason and needs the permission to view identity documents.',
      fileCreated: 'Created',
      signedBy: { 0: 'Not signed', 1: 'Signed by the registrant', 2: 'Signed by the registrar' },
      failed: 'Could not load the VNNIC status',
      notVn: 'Not a .vn domain: the dossier is not sent to VNNIC.',
      registrar: 'Registrar',
    },
    blocker: {
      signed1: '{{name}} (signed by the registrant)',
      signed2: '{{name}} (signed by the registrar)',
      or: ' or ',
      rejected: '{{name}}: {{problem}}',
      NO_DOC_TYPE: 'this operation has no VNNIC dossier kind',
      NO_VERIFICATION: 'the registrant is not verified',
      METHOD_NOT_ACCEPTED: 'VNNIC does not accept the registrant’s verification method',
      CASE_UNSUPPORTED: 'the domain’s case does not work with this verification method',
      NO_CASE: 'VNNIC has no dossier rule for this operation and verification method',
      EKYC_REQUEST_ID: 'the eKYC request ID is missing',
      EKYC_SCORE: 'the eKYC result (face match) is missing',
      SIGNER_UNKNOWN: 'the signer and signing date of the signed file are unknown',
      OTHER: 'staff need to check the dossier',
    },
    versions: {
      title: 'Dossier history',
      failed: 'Could not load the dossier history',
      created: 'created {{date}}',
      completed: 'completed {{date}}',
      current: 'Current',
    },
  },
  overview: {
    expiry: 'Expiry',
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',
    expired_one: 'Expired {{count}} day ago',
    expired_other: 'Expired {{count}} days ago',
    renewPrice: 'Renewal, 1 year',
    renewPriceHint: 'Default price book, before tax',
    noPrice: 'No renewal price in the default price book',
    dossier: 'Dossier files',
    filesComplete: 'Every required file present',
    filesMissing_one: '{{count}} file missing',
    filesMissing_other: '{{count}} files missing',
    versionN: 'version {{n}}',
    registration: 'Registration record',
    registrationHint: 'Our record and what the registry said, side by side.',
    ascii: 'Domain (ASCII)',
    unicode: 'Domain (Unicode)',
    domainId: 'Domain ID',
    roid: 'ID at the registry',
    notRead: 'Not read from the registry yet',
    createdAt: 'Created here',
    registryCreated: 'Created at the registry',
    registryExpiry: 'Expires at the registry',
    paidThrough: 'Paid through',
    tld: 'TLD',
    registry: 'Registry',
    sponsor: 'Sponsoring registrar at the registry',
    lifecycle: 'State',
    snapshot: 'Last read from the registry',
    record: 'Record',
    recordValue: 'version {{version}} · {{at}}',
    subjectDossier: 'Registrant & dossier',
    subjectVersion: 'Registrant version in the dossier',
    dossierLine: 'Dossier',
    verification: 'Verification',
    completed: 'Dossier completed',
    openSubject: 'See the registrant',
    openDossier: 'See the dossier',
    dnsSecurity: 'DNS & security',
    manage: 'Manage',
    nameservers: 'Name servers',
    none: 'None',
    dnssec: 'DNSSEC',
    dnssecOn_one: 'On · {{count}} DS record',
    dnssecOn_other: 'On · {{count}} DS records',
    dnssecOff: 'Off',
    transferLock: 'Transfer lock',
    autoRenew: 'Auto-renew',
    on: 'On',
    off: 'Off',
    related: 'Related work & transactions',
    relatedHint: 'Orders, invoices and open work of {{domain}}.',
    relatedKind: 'What',
    relatedRef: 'Reference',
    relatedState: 'State',
    openRow: 'Open',
    order_one: 'Order · {{operation}} {{count}} year',
    order_other: 'Order · {{operation}} {{count}} years',
    invoice: 'Invoice',
    creditNote: 'Credit note',
    workOpen: 'Open',
    loading: 'Loading…',
    noRelated: 'No order, invoice or open work for this domain',
  },
  dns: {
    title: 'Name servers at the registry',
    hint: 'The delegation at the registry, ours beside what the registry answered at the last check.',
    change: 'Change name servers',
    driftTitle: 'The registry holds other name servers',
    driftAddressesTitle: 'The registry holds other name-server IP addresses',
    driftBody: 'Found at the check of {{at}}. It is being reconciled: check with the customer before changing anything.',
    ours: 'Ours',
    ipv4: 'IPv4: {{list}}',
    ipv6: 'IPv6: {{list}}',
    addressesDiffer: 'Different IP addresses',
    atRegistry: 'At the registry',
    notRead: 'Not read yet',
    match: 'Comparison',
    unknown: 'Not compared',
    same: 'Same',
    missingAtRegistry: 'Not at the registry',
    onlyAtRegistry: 'Only at the registry',
    delegation: 'Delegation',
    state: 'DNS state',
    hostObjects: 'Registry keeps host objects',
    yes: 'Yes',
    no: 'No',
    snapshot: 'Last read from the registry',
    registry: 'Registry',
    zoneTitle: 'DNS records are not kept here',
    zoneBody: 'The domain points to the name servers above; its records (A, MX, TXT…) are managed on those servers.',
  },
  fees: {
    title: 'Fees by operation',
    hint: 'A calculation from this TLD’s prices (default price book); it creates no order and charges nothing.',
    operation: 'Operation',
    years: 'Term',
    yearsN_one: '{{count}} year',
    yearsN_other: '{{count}} years',
    failed: 'Could not calculate the fees',
    none: 'No price for this TLD',
  },
  finance: {
    invoiced: 'Invoiced',
    invoices_one: '{{count}} invoice',
    invoices_other: '{{count}} invoices',
    held: 'Held now',
    heldHint: 'Funds reserved for an operation in progress',
    refunded: 'Refunded',
    refunds_one: '{{count}} refund',
    refunds_other: '{{count}} refunds',
    priceTitle: 'Price, cost & next renewal',
    priceHint: 'One renewal year in the default price book against the registry cost.',
    sell: 'Selling price (before tax)',
    cost: 'Registry cost',
    margin: 'Difference',
    total: 'Customer pays (with tax)',
    autoRenew: 'Auto-renew',
    autoRenewOn_one: 'On · {{count}} year',
    autoRenewOn_other: 'On · {{count}} years',
    off: 'Off',
    paidThrough: 'Paid through',
    noPrice: 'No renewal price in the default price book.',
    note: 'Paid does not mean the registry operation succeeded: the registry state is on the Registry tab.',
  },
  renewal: {
    title: 'Auto-renew & reminders',
    hint: 'Auto-renew neither charges nor renews at once; reminders follow the expiry notice rules.',
    renew: 'Renew',
    autoRenew: 'Auto-renew',
    on_one: 'On · {{count}} year',
    on_other: 'On · {{count}} years',
    off: 'Off',
    registryExpiry: 'Expires at the registry',
    paidThrough: 'Paid through',
    lastNotice: 'Last reminder',
    noNotice: 'None sent yet',
    failed: 'Could not load the reminders',
    note: 'Grace and redemption dates depend on the TLD’s rules; they are not worked out from the expiry date here.',
  },
  security: {
    title: 'Locks & transfer conditions',
    hint: 'The transfer code is never shown here; issuing one is audited and needs its own action.',
    lock: 'Lock transfer',
    unlock: 'Unlock transfer',
    code: 'Transfer code',
    transferLock: 'Transfer lock',
    on: 'On',
    off: 'Off',
    ourLocks: 'Registrar locks',
    until: 'until {{date}}',
    none: 'None',
    codeSet: 'Issued {{date}}',
    noCode: 'None issued',
    legal: 'Legal locks',
    transfer: 'Registrar transfer',
    registrant: 'Registrant change',
    changeRegistrant: 'Change registrant',
    noteTitle: 'A transfer does not change the registrant',
    note: 'Moving to another registrar and changing the registrant are separate procedures, each with its own checks.',
  },
  registryState: {
    title: 'Registry & sponsoring registrar',
    hint: 'What the registry answered at the last check.',
    registry: 'Registry',
    sponsor: 'Sponsoring registrar',
    roid: 'ID at the registry',
    read: 'Read',
    notRead: 'Not read yet',
    created: 'Created at the registry',
    connection: 'Connection',
    diffTitle: 'Ours ↔ the registry',
    diffHint: 'Neither side overwrites the other until the difference is checked.',
    field: 'What',
    ours: 'Ours',
    theirs: 'At the registry',
    result: 'Result',
    match: { same: 'Same', differs: 'Differs', unknown: 'Not compared' },
    expiry: 'Expiry',
    statuses: 'Statuses',
    nameservers: 'Name servers',
    glue: 'Name server addresses (glue)',
    issuesHidden: 'Your role does not see the reconciliation findings.',
    noIssues: 'Reconciliation found no difference for this domain.',
    detected: 'found {{date}}',
    openIssue: 'Open',
    issueTypes: { NAMESERVER: 'Name servers differ', EXISTENCE: 'Domain missing or unexpected at the registry', SPONSORSHIP: 'Sponsored by another registrar', UNKNOWN_OUTCOME: 'Command without a result' },
    issueStates: { OPEN: 'Open', AUTO_RECONCILING: 'Being reconciled', MANUAL_REVIEW: 'Needs staff', RESOLVED: 'Resolved', IGNORED: 'Dismissed' },
    sync: {
      button: 'Sync with the registry',
      busy: 'Reading the registry…',
      done: 'Synced at {{time}}.',
      IN_SYNC: 'Matches the registry.',
      REFRESHED: 'Our copy of the registry facts was updated: {{fields}}.',
      ISSUES_OPENED: 'Differences to review: {{fields}} (see below). Neither side was overwritten.',
    },
  },
  syncCompare: {
    title: 'Versions before a sync',
    hint: 'A sync sends the dossier with the registrant version it holds; it does not mark itself acknowledged.',
    subject: 'Registrant version',
    subjectValue: 'dossier v{{pinned}} · registrant now v{{current}}',
    behind: 'the registrant changed since',
    dossier: 'Dossier',
    sent: 'Sent to VNNIC',
    notSent: 'Not sent yet',
    notSentYet: 'the current version is not sent yet',
    state: 'Sync state',
    compliance: 'Compliance',
    complianceStates: { UNKNOWN: 'Not assessed', COMPLIANT: 'Compliant', ACTION_REQUIRED: 'Action required', BLOCKED: 'Blocked', SUSPENDED: 'Suspended' },
    note: 'Sending again or reporting is done from the VNNIC pages below.',
  },
  notes: {
    title: 'Internal notes',
    hint: 'Staff notes on this domain’s dossier; never sent to the customer.',
    add: 'Add a note',
    none: 'No note yet.',
    notQueued: 'Notes are kept on dossiers in the review queue; this dossier is not there.',
  },
  subjectPage: {
    verified: 'Verified',
    notVerified: 'Not verified',
    needsEkyc: 'Not verified (eKYC needed)',
    needsEkycNote: 'An individual is verified by eKYC only: a signed declaration no longer counts for a new operation. Registered domains keep their dossiers.',
    domains_one: '{{count}} domain',
    domains_other: '{{count}} domains',
    version: 'Version {{n}}',
    verification: 'Identity verification',
    noVerification: 'The current version is not verified yet (eKYC or digitally signed documents).',
    versionsHint: 'Earlier versions stay; each domain keeps the version it was copied from.',
    controlledTitle: 'Controlled updates',
    controlledBody: 'Editing the registrant makes a new version; domains keep their own contacts until staff sync them (see “Domains using this subject”).',
  },
  record: {
    title: 'Record & context',
    version: 'Record version',
    updated: 'Updated',
    unresolved: 'Commands without a result',
    history: 'See the history',
  },
} as const;
